/**
 * Invoices API service for managing billing invoices.
 * @module lib/api/invoices.api
 */

import {
  BillingRunPreview,
  BillingRunQueue,
  CreateBillingRunPayload,
  CreateBillingRunResponse,
  CreateInvoice,
  Invoice,
  PaginatedResponse,
  PaginationParams,
  UpdateInvoiceInput,
  WorkspaceInvoiceRollupItem,
} from '@scrambl/shared'

import { trackActivationMilestone } from '@/lib/analytics'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

class InvoiceApiService extends BaseApi<
  Invoice,
  CreateInvoice,
  UpdateInvoiceInput
> {
  constructor() {
    super({
      baseEndpoint: '/invoices',
      responseNormalization: true,
    })
  }

  // Override create to add deletedAt field required by backend
  async create(data: CreateInvoice): Promise<Invoice> {
    const invoiceData = {
      ...data,
      deletedAt: null,
    }

    return super.create(invoiceData as CreateInvoice)
  }

  // Alias methods for backward compatibility
  async getInvoices(
    params: Partial<PaginationParams> = {},
  ): Promise<PaginatedResponse<Invoice>> {
    return this.list(params)
  }

  async getRevenueByProject(params: {
    from: string
    to: string
    entityIds?: string[]
    projectIds?: string[]
  }): Promise<Array<{ projectId: string; totalAmount: number }>> {
    const query = new URLSearchParams({
      from: params.from,
      to: params.to,
    })
    if (params.entityIds?.length)
      query.set('entityIds', params.entityIds.join(','))
    if (params.projectIds?.length)
      query.set('projectIds', params.projectIds.join(','))
    return this.makeRequest<Array<{ projectId: string; totalAmount: number }>>(
      `${this.config.baseEndpoint}/revenue-by-project?${query.toString()}`,
    )
  }

  async getArAging(params: {
    entityIds?: string[]
    projectIds?: string[]
  } = {}): Promise<{
    asOf: string
    buckets: Array<{ label: string; amount: number }>
    totalOverdue: number
  }> {
    const query = new URLSearchParams()
    if (params.entityIds?.length)
      query.set('entityIds', params.entityIds.join(','))
    if (params.projectIds?.length)
      query.set('projectIds', params.projectIds.join(','))
    const qs = query.toString()
    return this.makeRequest(`${this.config.baseEndpoint}/ar-aging${qs ? `?${qs}` : ''}`)
  }

  async getInvoice(id: string): Promise<Invoice> {
    return this.findById(id)
  }

  async createInvoice(data: CreateInvoice): Promise<Invoice> {
    return this.create(data)
  }

  async updateInvoice(id: string, data: UpdateInvoiceInput): Promise<Invoice> {
    return this.update(id, data)
  }

  async deleteInvoice(id: string): Promise<void> {
    return this.delete(id)
  }

  async sendInvoice(
    id: string,
    data: {
      to: string
      subject: string
      message?: string
      authorizationOverrideReason?: string
      approvalOverrideReason?: string
      acknowledgeAmountWarning?: boolean
    },
  ): Promise<{ success: boolean; invoice: Invoice }> {
    const result = await this.makeRequest<{
      success: boolean
      invoice: Invoice
    }>(`${this.config.baseEndpoint}/${id}/send`, {
      method: 'POST',
      body: data,
    })

    // Last step of the activation chain — an invoice actually delivered to a
    // client, not a draft saved.
    if (result.success) {
      trackActivationMilestone('invoice_sent')
    }

    return result
  }

  async downloadInvoicePdf(id: string): Promise<Blob> {
    return this.makeBlobRequest(`${this.config.baseEndpoint}/${id}/pdf`, {
      acceptType: 'application/pdf',
    })
  }

  async recordPayment(
    id: string,
    data: {
      amount: number
      paymentDate: string
      paymentMethod?: string
      notes?: string
    },
  ): Promise<Invoice> {
    return this.makeRequest<Invoice>(
      `${this.config.baseEndpoint}/${id}/payment`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  /**
   * Client billing run — every unbilled, approved, billable hour for one
   * client across all of their projects, grouped for review.
   */
  async getClientBillingRunPreview(
    entityId: string,
    params: {
      periodStart?: string
      periodEnd?: string
      projectIds?: string[]
    } = {},
  ): Promise<BillingRunPreview> {
    const query = new URLSearchParams()
    if (params.periodStart) query.set('periodStart', params.periodStart)
    if (params.periodEnd) query.set('periodEnd', params.periodEnd)
    if (params.projectIds?.length)
      query.set('projectIds', params.projectIds.join(','))
    const qs = query.toString()
    return this.makeRequest<BillingRunPreview>(
      `${this.config.baseEndpoint}/billing-run/client/${entityId}${
        qs ? `?${qs}` : ''
      }`,
    )
  }

  async createClientBillingRun(
    entityId: string,
    payload: CreateBillingRunPayload,
  ): Promise<CreateBillingRunResponse> {
    return this.makeRequest<CreateBillingRunResponse>(
      `${this.config.baseEndpoint}/billing-run/client/${entityId}`,
      { method: 'POST', body: payload },
    )
  }

  /**
   * Cross-client unbilled-charges queue (invoicing-ops Phase 4, D1): the
   * billing run's expense machinery run org-wide for a period.
   */
  async getUnbilledChargesQueue(params: {
    periodStart?: string
    periodEnd?: string
  }): Promise<BillingRunQueue> {
    const query = new URLSearchParams()
    if (params.periodStart) query.set('periodStart', params.periodStart)
    if (params.periodEnd) query.set('periodEnd', params.periodEnd)
    const qs = query.toString()
    return this.makeRequest<BillingRunQueue>(
      `${this.config.baseEndpoint}/billing-run/queue${qs ? `?${qs}` : ''}`,
    )
  }

  /**
   * Monthly workspace invoice roll-up: the period's invoices with latest
   * approval status attached. period is YYYY-MM.
   */
  async getWorkspaceRollup(
    period: string,
  ): Promise<WorkspaceInvoiceRollupItem[]> {
    return this.makeRequest<WorkspaceInvoiceRollupItem[]>(
      `${this.config.baseEndpoint}/workspace-rollup?period=${encodeURIComponent(period)}`,
    )
  }

  /**
   * Attach expenses onto an existing draft invoice. 409 means some expenses
   * are already billed on another live invoice.
   */
  async attachExpensesToInvoice(
    invoiceId: string,
    expenseIds: string[],
  ): Promise<Invoice> {
    return this.makeRequest<Invoice>(
      `${this.config.baseEndpoint}/${invoiceId}/expenses`,
      { method: 'POST', body: { expenseIds } },
    )
  }

  async bulkDeleteInvoices(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(
      `${this.config.baseEndpoint}/bulk-delete`,
      {
        method: 'POST',
        body: { ids },
      },
    )
  }
}

// Export singleton instance using factory
export const invoiceApi = createSingletonApi(InvoiceApiService)

// Export class for backward compatibility
export { InvoiceApiService as InvoiceApi }
